import { useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useGetVisibilityKpisQuery } from 'src/context/api/competitorsMonitoring/api';
import { fDate } from 'src/utils/format-time';
import { CountryItem } from 'src/components/country-bar/types';
import { deriveCountryCode } from './countryCode';

// Import du contexte pour vérifier s'il existe
import { VisibilityContext } from '../components/VisibilityProvider';

export const useCountryData = () => {
    // Essayer d'utiliser le contexte si disponible
    const context = useContext(VisibilityContext);

    // Si le contexte est disponible, l'utiliser
    if (context) {
        return {
            countriesData: context.countriesData,
            isLoading: context.isLoadingBaseData,
        };
    }

    // Sinon, fallback vers l'ancien comportement (standalone)
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);

    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;

    // Fetch competitive intelligence data to get countries
    const { data: visibilityData, isFetching: isLoadingBaseData } = useGetVisibilityKpisQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: filterStore.timestep,
        devices: filterStore.devices,
        countries: filterStore.countries,
        keywords: [],
        platforms: filterStore?.platforms,
        workspaceId: workspace?.id,
    }, {
        skip: skipQuery,
    });

    // Extract countries from competitive intelligence data
    const ciItems: any[] = useMemo(() =>
        Array.isArray(visibilityData) ? visibilityData : [],
        [visibilityData]
    );

    const countriesList = useMemo(() => {
        const uniqueCountryNames = Array.from(new Set(ciItems.map((it: any) => it?.country_name).filter(Boolean)));
        return uniqueCountryNames.map((name: string) => ({
            country_name: name,
            country_code: deriveCountryCode(name),
        }));
    }, [ciItems]);

    // Map countries with their competitive levels
    const countryLevelByName = useMemo(() => {
        const severityRank: Record<string, number> = { low: 0, active: 1, high: 2 };
        const levels = new Map<string, string>();
        for (const item of ciItems) {
            const countryName: string | undefined = (item?.country_name ?? item?.country) as string | undefined;
            const level: string | undefined = (item?.level ?? item?.abs_level ?? item?.severity_level) as string | undefined;
            if (!countryName || !level) continue;
            const current = levels.get(countryName);
            if (!current || (severityRank[level] ?? -1) > (severityRank[current] ?? -1)) {
                levels.set(countryName, level);
            }
        }
        return levels;
    }, [ciItems]);

    const shareByCountry = useMemo(() => {
        const map = new Map<string, number>();
        for (const item of ciItems) {
            if (!item?.country_name) continue;
            const share =
                typeof item?.brand_traffic_share === 'number'
                    ? item.brand_traffic_share
                    : (typeof item?.share_of_brand_searches === 'number'
                        ? item.share_of_brand_searches
                        : null);
            if (typeof share === 'number') {
                map.set(item.country_name, share);
            }
        }
        return map;
    }, [ciItems]);

    const countriesData: CountryItem[] = useMemo(() => {
        return countriesList
            .map(country => ({
                ...country,
                level: countryLevelByName.get(country.country_name),
                share_of_brand_searches: shareByCountry.get(country.country_name) ?? null,
            }))
            .sort((a, b) => (b.share_of_brand_searches ?? 0) - (a.share_of_brand_searches ?? 0));
    }, [countriesList, countryLevelByName, shareByCountry]);


    return {
        countriesData,
        isLoading: isLoadingBaseData,
    };
};
